'use client'

import { Download24, QrCode24, X24 } from '@vinted/monochrome-icons'
import {
  Bubble,
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Image,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { Fragment, MouseEvent, useEffect } from 'react'
import { FormattedDate } from 'react-intl'

import { useDropOffPointMapContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { UnknownErrorModal } from '@marketplace-web/pay-identity/pay-identity-kyc-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  downloadBase64File,
  downloadShippingLabel,
  transformDigitalLabelResponse,
  getDigitalLabel,
  clickEvent,
  selfServiceViewScreenEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { colorCodeToHex } from '@marketplace-web/vendor-abstractions/web-ui-util'
import {
  getDefaultShippingAddress,
  transformDefaultShippingAddress,
} from '@marketplace-web/address/address-data'
import { OfferBanner } from '@marketplace-web/ads/ads-feature'

import { truncateString } from '../../utils/string'

type Props = {
  isOpen: boolean
  onClose: () => void
  onDropOffPointModalOpen: () => void
  offerBanner: {
    isVisible: boolean
    isMultipleOffers: boolean
    isLoading: boolean
    onClick: () => void
  } | null
}

const VISIBLE_THUMBNAILS_LIMIT = 3
const MAX_CHARACTERS = 20
const INTERNAL_LINK_CLASS_NAME = 'msg-status__link react-drop-off-point-map-link'

const DigitalLabelModal = ({ isOpen, onClose, onDropOffPointModalOpen, offerBanner }: Props) => {
  const translate = useTranslate('conversation.digital_label.modal')
  const { track } = useTracking()

  const { conversation } = useConversationContext()
  const { fetchNearbyDropOffPoints } = useDropOffPointMapContext()

  const transactionId = conversation?.transaction?.id

  const {
    fetch: fetchDigitalLabel,
    transformedData: digitalLabel,
    isLoading,
    error,
  } = useFetch(getDigitalLabel, response => transformDigitalLabelResponse(response))

  const {
    fetch: fetchDefaultShippingAddress,
    transformedData: defaultShippingAddress,
    isLoading: isDefaultShippingAddressLoading,
  } = useFetch(getDefaultShippingAddress, transformDefaultShippingAddress)

  useEffect(() => {
    fetchDefaultShippingAddress()
  }, [fetchDefaultShippingAddress])

  useEffect(() => {
    if (!isOpen || !defaultShippingAddress || isDefaultShippingAddressLoading) return

    fetchNearbyDropOffPoints({ address: defaultShippingAddress })
  }, [defaultShippingAddress, isDefaultShippingAddressLoading, isOpen, fetchNearbyDropOffPoints])

  useEffect(() => {
    if (!transactionId || !isOpen) return

    fetchDigitalLabel({ transactionId })
  }, [transactionId, isOpen, fetchDigitalLabel])

  useEffect(() => {
    if (!isOpen || !transactionId) return

    const targetDetails = {
      screen: 'digital_label',
      transactionId,
    }

    track(selfServiceViewScreenEvent(targetDetails))
  }, [track, isOpen, transactionId])

  const handleLinkClickTracking = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return

    const url = event.target.href
    const eventTargetDetails = JSON.stringify({ transaction_id: transactionId, url })

    track(
      clickEvent({
        target: 'hyperlink',
        screen: 'digital_label',
        targetDetails: eventTargetDetails,
      }),
    )
  }

  const handleLinkClick = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return
    if (!(event.target.className === INTERNAL_LINK_CLASS_NAME)) {
      handleLinkClickTracking(event)

      return
    }

    event.preventDefault()

    fetchNearbyDropOffPoints({ address: defaultShippingAddress })

    onDropOffPointModalOpen()
  }

  function renderQrCode() {
    return (
      <Cell>
        <div className="u-flexbox u-flex-direction-column u-align-items-center">
          <Text as="h1" type="heading" text={translate('title')} alignment="center" />
          <div className="digital-label-modal__image">
            <span role="none" onClick={handleLinkClick}>
              <Text
                text={digitalLabel?.headerSubtitle}
                alignment="center"
                html
                testId="digital-label-modal-header-subtitle"
                as="span"
              />
            </span>
            <Spacer size="large" />
            <Bubble styling="tight">
              <Image
                src={digitalLabel?.imageUrl}
                scaling="cover"
                testId="digital-label-modal-image"
                styling="rounded"
              />
            </Bubble>
            <Spacer size="large" />
          </div>
          {digitalLabel?.code && (
            <>
              <Text as="span" text={translate('code_title')} alignment="center" />
              <Text as="h1" type="heading" text={digitalLabel?.code} alignment="center" />
            </>
          )}
          {offerBanner && (
            <OfferBanner
              isVisible={offerBanner.isVisible}
              isMultipleOffers={offerBanner.isMultipleOffers}
              onClick={offerBanner.onClick}
            />
          )}
        </div>
      </Cell>
    )
  }

  function renderShippingCarrier() {
    return (
      <Cell
        title={translate('shipping_carrier')}
        body={digitalLabel?.carrier.name}
        suffix={<Image src={digitalLabel?.carrier.iconUrl} size="medium" styling="rounded" />}
      />
    )
  }

  function renderOrderTitle() {
    if (!digitalLabel) return null

    const { items } = digitalLabel

    if (items.length > 1) {
      const count = items.length.toString()

      return <Text as="span" text={translate('order_count', { count })} />
    }

    return <Text as="span" text={items[0]?.name} />
  }

  function renderOrderPhotos() {
    if (!digitalLabel) return null

    return (
      <>
        {digitalLabel.items.length > VISIBLE_THUMBNAILS_LIMIT && (
          <Image
            label={`+${digitalLabel.items.length - VISIBLE_THUMBNAILS_LIMIT}`}
            styling="rounded"
            size="medium"
            scaling="cover"
            color={colorCodeToHex('greyscale-level-4')}
          />
        )}
        {digitalLabel.items.map((item, index) => {
          if (index >= VISIBLE_THUMBNAILS_LIMIT) return null

          return (
            <Fragment key={item.name}>
              <Spacer orientation="vertical" />
              <Image src={item.imageUrl} styling="rounded" size="medium" scaling="cover" />
            </Fragment>
          )
        })}
      </>
    )
  }

  function renderOrder() {
    return (
      <Cell title={translate('order')} body={renderOrderTitle()} suffix={renderOrderPhotos()} />
    )
  }

  function renderRecipient() {
    return (
      <Cell
        title={translate('recipient')}
        suffix={
          <Text
            as="span"
            text={
              digitalLabel?.receiverName &&
              truncateString(digitalLabel.receiverName, MAX_CHARACTERS)
            }
          />
        }
      />
    )
  }

  function renderDeliveryEstimate() {
    return (
      <Cell
        title={translate('delivery_estimate')}
        suffix={<Text as="span" alignment="right" text={digitalLabel?.deliveryEstimate} />}
      />
    )
  }

  function renderQrCodeExpiration() {
    return (
      <Cell
        title={digitalLabel?.labelExpiresTitle}
        suffix={
          <Text
            as="span"
            text={<FormattedDate value={digitalLabel?.expirationDate} month="long" day="numeric" />}
          />
        }
      />
    )
  }

  function trackDownloadQrCodeClick() {
    const targetDetails = { transaction_id: transactionId }

    track(
      clickEvent({
        screen: 'digital_label',
        target: 'download_digital_label',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  async function handleDownloadQrCodeClick() {
    if (!transactionId) return

    trackDownloadQrCodeClick()

    const response = await downloadShippingLabel(transactionId)

    if ('errors' in response) return

    const base64Data = response.file.label
    const fileName = response.file.filename

    downloadBase64File({ base64Data, fileName })
  }

  function renderDownloadQrCode() {
    return (
      <Cell
        clickable
        prefix={<Icon name={QrCode24} color="greyscale-level-2" />}
        title={digitalLabel?.downloadLabelTitle}
        suffix={<Icon name={Download24} color="greyscale-level-1" />}
        onClick={handleDownloadQrCodeClick}
      />
    )
  }

  function renderContent() {
    if (isLoading || offerBanner?.isLoading) return <ContentLoader testId="digital-label-loader" />

    if (error) return <UnknownErrorModal show onSubmit={onClose} />

    return (
      <>
        <Navigation
          body={digitalLabel?.digitalLabelTitle}
          right={
            <Button
              styling="flat"
              onClick={onClose}
              iconName={X24}
              testId="close-qr-code-modal"
              inline
            />
          }
        />
        <Divider />
        <div className="modal-contained-content">
          {renderQrCode()}
          <SeparatedList separator={<Divider />}>
            {renderShippingCarrier()}
            {renderOrder()}
            {renderRecipient()}
            {renderDeliveryEstimate()}
            {renderQrCodeExpiration()}
            {renderDownloadQrCode()}
            <Spacer />
          </SeparatedList>
        </div>
      </>
    )
  }

  return (
    <Dialog show={isOpen} testId="digital-label-modal">
      {renderContent()}
    </Dialog>
  )
}

export default DigitalLabelModal
